<template>
  <v-card
    :loading="loading"
    rounded="xl"
    class="mb-4"
  >
    <v-list color="grey">
      <v-list-item :dense="$vuetify.breakpoint.xsOnly">
        <v-list-item-icon v-if="!($vuetify.breakpoint.xsOnly || $vuetify.breakpoint.smOnly)">
          <v-icon size="36">
            {{ icons.mdiTarget }}
          </v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title :style="!$vuetify.breakpoint.mdAndDown ? null : 'font-size:15px'">
            Ürün Hedefi
          </v-list-item-title>
          <v-list-item-subtitle class="text-caption">
            Hedefimiz: {{ uniqueProductsTarget }} ürün
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <v-card-actions class="grey">
      <v-row v-if="!$vuetify.breakpoint.mdAndDown" class="pt-3">
        <v-col cols="12">
          <v-select
            :disabled="loading"
            :items="tarihElemanlar"
            v-model="tarihDeger"
            label="Analiz Tarihi"
            rounded
            filled
            dense
            class="unique-products-date-select"
            background-color="nightDarkTon"
            @change="onDateChange"
          ></v-select>
        </v-col>
      </v-row>
      <v-row v-else dense>
        <v-col cols="12">
          <v-select
            :disabled="loading"
            :items="tarihElemanlar"
            v-model="tarihDeger"
            rounded
            filled
            dense
            hide-details
            class="unique-products-date-select unique-products-date-select--mobile"
            background-color="nightDarkTon"
            @change="onDateChange"
          ></v-select>
        </v-col>
      </v-row>
    </v-card-actions>

    <v-divider></v-divider>

    <v-card-text class="pt-4 unique-products-body">
      <div class="unique-products-bar" :class="{ 'unique-products-bar--loading': loading }">
        <div class="unique-products-bar__fill" :style="{ width: `${uniqueProductsAnimatedPercent}%` }"></div>
        <div class="unique-products-bar__center">
          {{ loading || !uniqueProductsNumbersVisible ? '' : uniqueProductsAnimatedCount }}
        </div>
        <div
          class="unique-products-bar__right"
          v-if="!loading && uniqueProductsRemaining > 0 && Number(uniqueProductsAnimatedCount) > 0"
        >
          {{ !uniqueProductsNumbersVisible ? '' : uniqueProductsAnimatedRemaining }}
        </div>
      </div>

      <div class="d-flex justify-end mt-2" v-if="!loading && uniqueProductsItems.length">
        <v-btn
          text
          small
          class="text-capitalize"
          @click.stop="toggleUniqueProductsTable"
        >
          <v-icon left>
            {{ uniqueProductsTableOpen ? icons.mdiChevronUp : icons.mdiChevronDown }}
          </v-icon>
          {{ uniqueProductsTableOpen ? 'Detayları Gizle' : 'Detayları Göster' }}
        </v-btn>
      </div>

      <div v-if="uniqueProductsTableOpen" class="unique-products-table-wrap">
        <v-data-table
          :headers="uniqueProductsHeaders"
          :items="uniqueProductsItems"
          :loading="loading"
          loader-height="2"
          dense
          fixed-header
          hide-default-footer
          :items-per-page="80"
          height="320"
          class="rounded-xl mt-2 unique-products-table"
        >
          <template #[`item.rowNo`]="{ index }">
            <div class="text-caption d-flex justify-center" style="min-width: 36px">
              {{ index + 1 }}
            </div>
          </template>

          <template #[`item.product`]="{ item }">
            <div class="d-flex align-start unique-products-product-cell py-1">
              <v-avatar color="white" size="24" class="mr-2 flex-shrink-0 mt-1">
                <img v-if="item.src" :src="item.src" />
                <div v-else class="text-caption">Ş</div>
              </v-avatar>
              <div class="unique-products-product-info">
                <div class="text-caption unique-products-product-code">{{ item.id }}</div>
                <div class="text-caption unique-products-product-text">{{ item.text }}</div>
              </div>
            </div>
          </template>

          <template #[`item.value`]="{ item }">
            <div class="text-caption white--text unique-products-value-cell">
              {{ item.value | currency }}
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { ref, nextTick, computed, onMounted } from '@vue/composition-api'

// Plugin
import gsap from 'gsap'
import moment from 'moment'

// icon
import { mdiTarget, mdiChevronDown, mdiChevronUp } from '@mdi/js'
import { postData } from '@utils'

export default {
  name: 'DashboardUniqueProductsTarget',
  props: {
    maxHeight: {
      type: Number,
      default: null,
    },
  },
  setup() {
    const tarihDeger = ref(null)
    const tarihElemanlar = ref([])

    const loading = ref(true)

    const uniqueProductsTarget = ref(80)
    const uniqueProductsItems = ref([])
    const uniqueProductsTableOpen = ref(false)
    const uniqueProductsAnimatedPercent = ref(0)
    const uniqueProductsAnimatedCount = ref(0)
    const uniqueProductsNumbersVisible = ref(false)
    const uniqueProductsAnim = { value: 0 }
    const uniqueProductsDevOverrideCount = ref(0) // ürünhedef sayısını test için

    const uniqueProductsHeaders = ref([
      { text: 'Sıra', value: 'rowNo', width: 40, sortable: false },
      { text: 'Ürün', value: 'product', sortable: false },
      { text: 'Tutar', value: 'value', align: 'end', width: 96, sortable: false },
    ])

    const uniqueProductsCount = computed(() => uniqueProductsItems.value.length)
    const uniqueProductsRemaining = computed(() =>
      Math.max(0, Number(uniqueProductsTarget.value) - uniqueProductsCount.value),
    )
    const uniqueProductsAnimatedRemaining = computed(() =>
      Math.max(0, Number(uniqueProductsTarget.value) - Number(uniqueProductsAnimatedCount.value)),
    )

    const toggleUniqueProductsTable = () => {
      uniqueProductsTableOpen.value = !uniqueProductsTableOpen.value
    }

    const animateUniqueProductsProgress = (finalCount = uniqueProductsCount.value) => {
      const target = Number(uniqueProductsTarget.value) || 0
      const next = Math.max(0, Math.min(Number(finalCount) || 0, target || 0))
      gsap.killTweensOf(uniqueProductsAnim)
      uniqueProductsAnim.value = 0
      uniqueProductsAnimatedPercent.value = 0
      uniqueProductsAnimatedCount.value = 0
      uniqueProductsNumbersVisible.value = true
      gsap.to(uniqueProductsAnim, {
        value: next,
        duration: 2.6,
        ease: 'power1.out',
        onUpdate: () => {
          const v = Math.round(uniqueProductsAnim.value)
          uniqueProductsAnimatedCount.value = v
          uniqueProductsAnimatedPercent.value = target ? (v * 100) / target : 0
        },
      })
    }

    const getMonthDateRange = (year, month) => {
      const startDate = moment([year, month])
      const endDate = moment(startDate).endOf('month')
      const start = moment(startDate).format('DD.MM.YYYY')
      const end = moment(endDate).format('DD.MM.YYYY')

      // Stable string value avoids v-select re-emitting a new array reference on layout changes
      tarihElemanlar.value.push({
        text: moment(startDate).format('MMMM YYYY'),
        value: `"${start}","${end}"`,
      })
    }

    const initDateRanges = () => {
      const d = new Date()
      for (let i = 0; i <= 11; i++) {
        getMonthDateRange(d.getFullYear(), d.getMonth())
        d.setMonth(d.getMonth() - 1)
      }
      if (tarihElemanlar.value.length && !tarihDeger.value) {
        tarihDeger.value = tarihElemanlar.value[0].value
      }
    }

    const fetchUniqueProducts = async () => {
      if (!tarihDeger.value) return

      loading.value = true
      uniqueProductsAnimatedPercent.value = 0
      uniqueProductsAnimatedCount.value = 0
      uniqueProductsNumbersVisible.value = false

      try {
        const response = await postData({
          method: 'dashboardUniqueProductsReport',
          daterange: `[${tarihDeger.value}]`,
        })

        if (response.error === 0) {
          const incoming = Array.isArray(response.data) ? response.data : []
          const override = Number(uniqueProductsDevOverrideCount.value) || 0
          uniqueProductsItems.value = override ? incoming.slice(0, override) : incoming
          uniqueProductsTarget.value = 80
          nextTick(() => animateUniqueProductsProgress(uniqueProductsItems.value.length))
        } else if (
          Number(response.error) === 1 &&
          typeof response.msg === 'string' &&
          response.msg.toLowerCase().includes('sonuç bulunamadı')
        ) {
          uniqueProductsItems.value = []
          uniqueProductsTarget.value = 80
          uniqueProductsNumbersVisible.value = true
          nextTick(() => animateUniqueProductsProgress(0))
        } else {
          throw new Error(response.msg)
        }
      } catch (e) {
        uniqueProductsItems.value = []
        uniqueProductsTarget.value = 80
        uniqueProductsNumbersVisible.value = true
        nextTick(() => animateUniqueProductsProgress(0))
      } finally {
        loading.value = false
      }
    }

    const onDateChange = () => {
      uniqueProductsTableOpen.value = false
      fetchUniqueProducts()
    }

    initDateRanges()

    onMounted(() => {
      fetchUniqueProducts()
    })

    return {
      loading,
      tarihDeger,
      tarihElemanlar,
      uniqueProductsTarget,
      uniqueProductsItems,
      uniqueProductsHeaders,
      uniqueProductsTableOpen,
      uniqueProductsAnimatedPercent,
      uniqueProductsAnimatedCount,
      uniqueProductsAnimatedRemaining,
      uniqueProductsNumbersVisible,
      uniqueProductsRemaining,
      toggleUniqueProductsTable,
      onDateChange,
      icons: {
        mdiTarget,
        mdiChevronDown,
        mdiChevronUp,
      },
    }
  },
}
</script>

<style lang="scss">
.unique-products-bar {
  position: relative;
  height: 44px;
  min-height: 44px;
  border-radius: 999px;
  background: #201c2d;
  overflow: hidden;
}
.unique-products-bar__fill {
  height: 100%;
  width: 0%;
  background: #0083e8;
  border-radius: 999px;
  will-change: width;
}
.unique-products-bar__center {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.5px;
  color: #ffffff;
}
.unique-products-bar__right {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  opacity: 0.9;
  color: #ffffff;
}

.unique-products-date-select {
  max-width: 340px;
}
.unique-products-date-select--mobile {
  max-width: 100%;
}

.unique-products-product-cell {
  min-width: 0;
  max-width: 100%;
}
.unique-products-product-info {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}
.unique-products-product-code {
  letter-spacing: 0.4px;
  color: #7eb8ff;
  line-height: 1.25;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.unique-products-product-text {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.25;
  font-size: 11px !important;
}
.unique-products-value-cell {
  text-align: right;
  white-space: nowrap;
  font-size: 11px !important;
  line-height: 1.25;
}

.unique-products-table-wrap {
  margin-top: 4px;
}

/* 3-column compact table + scrollbar */
.unique-products-table {
  table,
  .v-data-table table {
    table-layout: fixed;
    width: 100%;
  }

  thead th {
    vertical-align: middle !important;
    height: 48px;
  }

  thead th .v-data-table-header__content {
    display: flex;
    align-items: center;
    min-height: 32px;
  }

  th,
  td {
    padding-left: 6px !important;
    padding-right: 6px !important;
  }

  th:first-child .v-data-table-header__content,
  td:first-child {
    justify-content: center;
  }

  th:first-child,
  td:first-child {
    padding-left: 8px !important;
    padding-right: 4px !important;
  }

  th:last-child,
  td:last-child {
    padding-left: 4px !important;
    padding-right: 8px !important;
  }

  .v-data-table__wrapper {
    scrollbar-width: thin; /* Firefox */
    scrollbar-color: #6b6586 #2b263f; /* thumb track */
  }

  .v-data-table__wrapper::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #2b263f;
    border-radius: 10px;
  }

  .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: #6b6586;
    border-radius: 10px;
    border: 2px solid #2b263f;
  }

  .v-data-table__wrapper::-webkit-scrollbar-thumb:hover {
    background: #8c86a8;
  }
}
</style>
